import { useState } from 'react';
import type { Operaciones } from '../api/operaciones';
import { ErrorApi } from '../api/problema';
import { tokenCaptcha } from '../acceso/InvitacionCaptcha';
import { HojaRoja } from '../acceso/InvitacionHoja';
import { Campo } from '../componentes/Campo';
import { Desplegable } from '../componentes/Desplegable';
import { Icono } from '../componentes/Icono';
import { Interruptor } from '../componentes/Interruptor';
import { Segmentos } from '../componentes/Segmentos';
import { errorDeCampo, mensajeDeError } from '../logica/acceso';
import { useEsMovil } from '../marco/tamano';
import { RUTA_PORTAL_PARTNER } from '../rutas';
import { ERRORES, PCT_PROGRAMA, SOLICITUD_VACIA, TIPOS_PARTNER, condicionesPrograma, datosSolicitud, erroresDelPaso, etiquetasFiscales, paisesFiscales, type ErroresSolicitud, type FormSolicitud } from './logica';
import './partners.css';

/**
 * Solicitud pública para ser partner (1.23, `?pantalla=alta` del prototipo): hoja roja (§16) con «Portal de partners» bajo
 * el logo, en 3 pasos (tu cuenta, datos fiscales, condiciones) y «Solicitud enviada». Antibot invisible como las demás
 * altas públicas (G76): si falla, el error sale junto al botón sin borrar lo escrito y el pie de reCAPTCHA lleva los
 * enlaces de Google. `applyAsPartner` responde `202` también si el email ya existía, sin revelarlo (G1090).
 *
 * El código del partner llega con la aprobación: «Tu código, cuando te aprobemos» del prototipo no se pinta (G1096, C.57).
 * La dirección fiscal va en cuatro campos, Dirección, CP, Ciudad y País (G1095), y el país fiscal es el de esa dirección; el
 * campo País está hecho con el desplegable que ya había y **se ajustará al llegar C.57**. La solicitud no manda la versión
 * de las condiciones: la fija y la guarda el back (G1099).
 */
const TEXTOS: readonly [string, string][] = [
    ['Date de alta como partner', 'Con tu cuenta ves los negocios que traes y lo que cobras por ellos.'],
    ['Datos fiscales', 'Los usamos para tus liquidaciones y sus facturas. La forma jurídica, la razón social y el NIF solo los cambia Reservaria: escríbenos.'],
    ['Condiciones', 'Léelas antes de empezar. Son las mismas para todos los partners.'],
    ['Solicitud enviada', 'La revisamos y te avisamos por email. Cuando la aprobemos, tu enlace y tu código empezarán a atribuirte negocios.'],
];

export function SolicitudPartner({ ops, navegar }: { ops: Operaciones; navegar: (ruta: string) => void }) {
    const movil = useEsMovil();
    const [paso, setPaso] = useState(0);
    const [f, setF] = useState<FormSolicitud>(SOLICITUD_VACIA);
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [robot, setRobot] = useState(false);
    const [errorApi, setErrorApi] = useState<ErroresSolicitud>({});
    const [errorForm, setErrorForm] = useState<string | null>(null);
    const errores: ErroresSolicitud = { ...(intentado ? erroresDelPaso(paso, f) : {}), ...errorApi };
    const [titulo, sub] = TEXTOS[paso] ?? ['', ''];
    const etiquetas = etiquetasFiscales(f.forma);

    const poner = (cambio: Partial<FormSolicitud>) => {
        setF((x) => ({ ...x, ...cambio }));
        setRobot(false);
        setErrorApi({});
    };
    const ir = (n: number) => {
        setPaso(n);
        setIntentado(false);
        setRobot(false);
        setErrorForm(null);
    };

    const enviar = () => {
        setOcupado(true);
        setRobot(false);
        setErrorForm(null);
        void (async () => {
            try {
                await ops.applyAsPartner(datosSolicitud(f), await tokenCaptcha('partner'));
                setOcupado(false);
                ir(3);
            } catch (e) {
                setOcupado(false);
                if (e instanceof ErrorApi && e.codigo === 'captcha_required') {
                    setRobot(true);
                    return;
                }
                // La contraseña filtrada (G64) o un dato que la API no acepta: se vuelve al paso del campo.
                const campos: [keyof ErroresSolicitud, string, number][] = [
                    ['contrasena', 'password', 0],
                    ['email', 'email', 0],
                    ['telefono', 'phone', 0],
                    ['nif', 'tax_id', 1],
                    ['iban', 'iban', 1],
                    ['emailFacturas', 'billing_email', 1],
                ];
                for (const [clave, campo, suPaso] of campos) {
                    const texto = errorDeCampo(e, campo);
                    if (texto !== null) {
                        setErrorApi({ [clave]: texto });
                        ir(suPaso);
                        return;
                    }
                }
                setErrorForm(mensajeDeError(e, 'contrasena'));
            }
        })();
    };

    const seguir = () => {
        if (paso === 3) {
            navegar(RUTA_PORTAL_PARTNER);
            return;
        }
        if (Object.keys(erroresDelPaso(paso, f)).length > 0) {
            setIntentado(true);
            return;
        }
        if (paso === 2) {
            enviar();
            return;
        }
        ir(paso + 1);
    };

    return (
        <HojaRoja
            giro={12 + paso * 60}
            marca={{ icono: 'handshake', texto: 'Portal de partners' }}
            volver={paso > 0 && paso < 3 ? { texto: 'Volver', accion: () => { ir(paso - 1); } } : null}
            paso={paso + 1}
            totalPasos={3}
            etiquetaPaso={paso === 3 ? 'Listo' : undefined}
            titulo={titulo}
            sub={sub}
            error={errorForm}
            ocupado={ocupado}
            principal={{ texto: ['Seguir', 'Seguir', 'Enviar la solicitud', 'Ir al portal'][paso] ?? 'Seguir', icono: paso === 2 ? 'send' : 'arrow-right', ocupado: 'Enviando…', accion: seguir }}
            pie={
                paso === 2 ? (
                    <div className="rv-alta__legal">
                        {robot && (
                            <div role="alert" className="rv-acceso__error">
                                <Icono nombre="circle-alert" />
                                <span>{ERRORES.robot}</span>
                            </div>
                        )}
                        <span>
                            Este sitio está protegido por reCAPTCHA y se aplican la{' '}
                            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                                Política de privacidad
                            </a>{' '}
                            y las{' '}
                            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
                                Condiciones del servicio
                            </a>{' '}
                            de Google.
                        </span>
                    </div>
                ) : undefined
            }
        >
            {paso === 0 && (
                <div className="rv-acceso__campos">
                    <Campo etiqueta="Nombre y apellidos" autocompletar="name" mayusculas="words" valor={f.nombre} error={errores.nombre} onChange={(v) => { poner({ nombre: v }); }} />
                    <Campo etiqueta="Email" tipo="email" autocompletar="email" valor={f.email} error={errores.email} onChange={(v) => { poner({ email: v }); }} />
                    <Campo etiqueta="Teléfono" tipo="tel" valor={f.telefono} error={errores.telefono} onChange={(v) => { poner({ telefono: v }); }} />
                    <Campo etiqueta="Contraseña" tipo="password" autocompletar="new-password" valor={f.contrasena} error={errores.contrasena} ayuda="Mínimo 12 caracteres." onChange={(v) => { poner({ contrasena: v }); }} onEnter={seguir} />
                </div>
            )}
            {paso === 1 && (
                <div className="rv-acceso__campos">
                    <div className="rv-pt-forma">
                        <span>Forma jurídica</span>
                        <Segmentos
                            etiqueta="Forma jurídica"
                            movil={movil}
                            items={[
                                { etiqueta: 'Autónomo', icono: 'user' },
                                { etiqueta: 'Empresa', icono: 'building-2' },
                            ]}
                            valor={f.forma === 'company' ? 1 : 0}
                            onCambio={(i) => { poner({ forma: i === 1 ? 'company' : 'self_employed' }); }}
                        />
                    </div>
                    <Desplegable etiqueta="Tipo de partner" valor={f.tipo} opciones={TIPOS_PARTNER.map((t) => ({ valor: t.valor, texto: t.texto }))} onChange={(v) => { poner({ tipo: v as FormSolicitud['tipo'] }); }} />
                    <Campo etiqueta={etiquetas.nombre} autocompletar="organization" valor={f.nombreFiscal} error={errores.nombreFiscal} onChange={(v) => { poner({ nombreFiscal: v }); }} />
                    <Campo etiqueta={etiquetas.nif} mayusculas="characters" valor={f.nif} error={errores.nif} onChange={(v) => { poner({ nif: v }); }} />
                    <Campo etiqueta="Dirección fiscal" autocompletar="address-line1" valor={f.direccion.calle} error={errores.calle} onChange={(v) => { poner({ direccion: { ...f.direccion, calle: v } }); }} />
                    <div className="rv-pt-dos">
                        <Campo etiqueta="Código postal" autocompletar="postal-code" valor={f.direccion.cp} error={errores.cp} onChange={(v) => { poner({ direccion: { ...f.direccion, cp: v } }); }} />
                        <Campo etiqueta="Ciudad" autocompletar="address-level2" valor={f.direccion.ciudad} error={errores.ciudad} onChange={(v) => { poner({ direccion: { ...f.direccion, ciudad: v } }); }} />
                    </div>
                    <Desplegable etiqueta="País" valor={f.direccion.pais} opciones={paisesFiscales(f.direccion.pais)} error={errores.pais} onChange={(v) => { poner({ direccion: { ...f.direccion, pais: v } }); }} />
                    <Campo etiqueta="Email para las facturas" tipo="email" valor={f.emailFacturas} error={errores.emailFacturas} onChange={(v) => { poner({ emailFacturas: v }); }} />
                    <Campo etiqueta="IBAN para los pagos" mayusculas="characters" autocompletar="off" valor={f.iban} error={errores.iban} onChange={(v) => { poner({ iban: v }); }} onEnter={seguir} />
                </div>
            )}
            {paso === 2 && (
                <div className="rv-pt-condiciones">
                    {condicionesPrograma(PCT_PROGRAMA).map(([icono, texto]) => (
                        <div key={texto}>
                            <Icono nombre={icono} />
                            <span>{texto}</span>
                        </div>
                    ))}
                    <Interruptor etiqueta="Acepto las condiciones del programa" valor={f.acepta} error={errores.acepta} linea="ninguna" onChange={(v) => { poner({ acepta: v }); }} />
                    <span className="rv-pt-condiciones__version">Versión de octubre de 2026. Guardamos qué versión aceptas y cuándo.</span>
                </div>
            )}
        </HojaRoja>
    );
}
